/**
 * AgentTypeInfo component - displays information about agent types.
 */

import { AGENT_TYPE_DESCRIPTIONS, AgentType } from "@/types/rules";
import { Popover, Typography } from "@mui/material";
import { useState } from "react";

interface AgentTypeInfoProps {
  agentType?: AgentType;
  showAllTypes?: boolean;
}

/**
 * Info icon that shows popover with agent type descriptions.
 */
export function AgentTypeInfo({ agentType, showAllTypes = false }: AgentTypeInfoProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <span
        className="material-symbols-outlined cursor-help text-base text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
        onClick={handleClick}
        style={{ fontSize: "18px" }}
      >
        info
      </span>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            className: "mt-2 max-w-md",
          },
        }}
      >
        <div className="p-4">
          {showAllTypes ? (
            <div className="space-y-3">
              <Typography variant="subtitle2" className="font-bold text-slate-900 dark:text-white">
                Agent Types
              </Typography>
              {Object.entries(AGENT_TYPE_DESCRIPTIONS).map(([type, description]) => (
                <div key={type}>
                  <Typography
                    variant="body2"
                    className="font-semibold capitalize text-slate-700 dark:text-slate-300"
                  >
                    {type}
                  </Typography>
                  <Typography variant="body2" className="text-slate-600 dark:text-slate-400">
                    {description}
                  </Typography>
                </div>
              ))}
            </div>
          ) : agentType ? (
            <div>
              <Typography
                variant="subtitle2"
                className="mb-1 font-bold capitalize text-slate-900 dark:text-white"
              >
                {agentType}
              </Typography>
              <Typography variant="body2" className="text-slate-600 dark:text-slate-400">
                {AGENT_TYPE_DESCRIPTIONS[agentType]}
              </Typography>
            </div>
          ) : null}
        </div>
      </Popover>
    </>
  );
}
